import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, Share2, MapPin, Clock, Users, Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  short_description: string | null;
  long_description: string | null;
  main_image_url: string | null;
  gallery_images_urls: string[] | null;
  category_id: string | null;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  tour_categories?: {
    name: string;
    icon: string;
  };
}

interface Tour {
  id: string;
  name: string;
  slug: string;
  main_image_url: string | null;
  short_description: string | null;
  adult_price: number | null;
  child_price: number | null;
  location: string | null;
  duration: string | null;
  features: string[] | null;
  category_id: string | null;
  tour_categories?: {
    name: string;
    icon: string;
  };
}

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState<BlogPost[]>([]);
  const [relatedTours, setRelatedTours] = useState<Tour[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (slug) {
      fetchBlogPost(slug);
    }
  }, [slug]);

  const fetchBlogPost = async (postSlug: string) => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          tour_categories (
            name,
            icon
          )
        `)
        .eq('slug', postSlug)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching blog post:', error);
        return;
      }

      setBlog(data);

      // Fetch related posts from the same category
      if (data.category_id) {
        const { data: related } = await supabase
          .from('blog_posts')
          .select(`
            *,
            tour_categories (
              name,
              icon
            )
          `)
          .eq('category_id', data.category_id)
          .eq('is_active', true)
          .neq('id', data.id)
          .limit(3);

        setRelatedBlogs(related || []);

        // Fetch related tours from the same category
        const { data: tours } = await supabase
          .from('tours')
          .select(`
            id,
            name,
            slug,
            main_image_url,
            short_description,
            adult_price,
            child_price,
            location,
            duration,
            features,
            category_id,
            tour_categories (
              name,
              icon
            )
          `)
          .eq('category_id', data.category_id)
          .eq('is_active', true)
          .limit(3);

        setRelatedTours(tours || []);
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog?.title,
          text: blog?.short_description || blog?.title,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled sharing
      }
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link copied",
        description: "The post link has been copied to clipboard",
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading post...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!blog) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Post not found</h1>
            <p className="text-muted-foreground mb-6">
              The post you're looking for doesn't exist or has been deleted.
            </p>
            <Link to="/blog">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="bg-secondary/30 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-primary hover:underline">
              Home
            </Link>
            <span className="text-muted-foreground">/</span>
            <Link to="/blog" className="text-primary hover:underline">
              Blog
            </Link>
            <span className="text-muted-foreground">/</span>
            <span className="text-muted-foreground">{blog.title}</span>
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <article className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Link to="/blog">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Blog
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleShare}>
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>

            <div className="flex items-center gap-2 mb-4">
              {blog.tour_categories && (
                <Badge variant="outline">{blog.tour_categories.name}</Badge>
              )}
              {blog.is_featured && (
                <Badge variant="default">Featured</Badge>
              )}
            </div>

            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              {blog.title}
            </h1>

            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(blog.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {blog.short_description && (
              <p className="text-xl text-muted-foreground mt-6 leading-relaxed">
                {blog.short_description}
              </p>
            )}
          </div>

          {/* Main Image */}
          {blog.main_image_url && (
            <div className="mb-8">
              <img
                src={blog.main_image_url}
                alt={blog.title}
                className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          )}

          {/* Content */}
          {blog.long_description && (
            <div className="prose prose-lg max-w-none mb-12">
              <div
                dangerouslySetInnerHTML={{ __html: blog.long_description }}
                className="text-foreground leading-relaxed"
              />
            </div>
          )}

          {/* Gallery */}
          {blog.gallery_images_urls && blog.gallery_images_urls.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Image Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {blog.gallery_images_urls.map((image, index) => (
                  <div key={index} className="aspect-video overflow-hidden rounded-lg">
                    <img
                      src={image}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Related Tours */}
      {relatedTours.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-4">Related Tours</h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Discover incredible tours that share similar characteristics with this content
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {relatedTours.map((tour) => (
                <Card key={tour.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                  {tour.main_image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={tour.main_image_url}
                        alt={tour.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      {tour.tour_categories && (
                        <Badge variant="secondary" className="text-xs">
                          {tour.tour_categories.name}
                        </Badge>
                      )}
                      {tour.adult_price && (
                        <div className="text-right">
                          <span className="text-lg font-bold text-primary">
                            ${tour.adult_price}
                          </span>
                          <span className="text-sm text-muted-foreground">/person</span>
                        </div>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                      <Link to={`/tours/${tour.slug}`}>
                        {tour.name}
                      </Link>
                    </h3>
                    
                    {tour.short_description && (
                      <p className="text-muted-foreground mb-4 line-clamp-2 text-sm">
                        {tour.short_description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                      {tour.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          <span className="truncate">{tour.location}</span>
                        </div>
                      )}
                      {tour.duration && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{tour.duration}</span>
                        </div>
                      )}
                    </div>

                    {tour.features && tour.features.length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-1">
                          {tour.features.slice(0, 3).map((feature, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                          {tour.features.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{tour.features.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <Link to={`/tours/${tour.slug}`}>
                      <Button className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        View Tour
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Posts */}
      {relatedBlogs.length > 0 && (
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {relatedBlogs.map((relatedBlog) => (
                <Card key={relatedBlog.id} className="group hover:shadow-lg transition-all duration-300">
                  {relatedBlog.main_image_url && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img
                        src={relatedBlog.main_image_url}
                        alt={relatedBlog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    {relatedBlog.tour_categories && (
                      <Badge variant="outline" className="mb-2">
                        {relatedBlog.tour_categories.name}
                      </Badge>
                    )}
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                      <Link to={`/blog/${relatedBlog.slug}`}>
                        {relatedBlog.title}
                      </Link>
                    </h3>
                    {relatedBlog.short_description && (
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {relatedBlog.short_description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {new Date(relatedBlog.created_at).toLocaleDateString('en-US')}
                      </div>
                      <Link to={`/blog/${relatedBlog.slug}`}>
                        <Button variant="ghost" size="sm">
                          Read more
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default BlogDetail;